import { graphql } from '../gql/index.ts';
import { useQuery } from 'urql';
import { SeminarCard } from './SeminarCard.tsx';
import { getFormattedSeminarDate } from './util.ts';
import { ErrorPage } from './ErrorPage.tsx';
import { useUserInfo } from './hooks/useUserInfo.ts';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { Loader } from '@progress/kendo-react-indicators';
import { useDeferredLoading } from '../shared/useDeferredLoading.ts';
import { webcalUrl } from '../shared/helpers/backendUrl';

const MyLecturerSeminarsQuery = graphql(/* GraphQL */ `
  query getMyLecturerSeminars($companyId: String!) {
    myLecturerSeminars(companyId: $companyId) {
      seminars {
        id
        title
        subtitle
        seminarNumber
        dateText
        description
        plainDescription
        locationText
        startDate
        endDate
        lecturers {
          name
        }
        categories {
          name
        }
      }
      count
    }
  }
`);

interface MyLecturerSeminarListProps {
  companyId: string;
}

export const MyLecturerSeminarList: React.FC<MyLecturerSeminarListProps> = ({
  companyId,
}) => {
  const [myLecturerSeminarsQueryResult] = useQuery({
    query: MyLecturerSeminarsQuery,
    variables: {
      companyId,
    },
    pause: companyId === undefined || companyId === null,
  });
  const { data } = myLecturerSeminarsQueryResult;
  const { myLecturerSeminars } = data || {};
  const { userInfo } = useUserInfo();

  const showLoader = useDeferredLoading(myLecturerSeminarsQueryResult.fetching);

  if (showLoader) {
    return (
      <div className="flex justify-center py-16">
        <Loader type={'infinite-spinner'} size={'large'} />
      </div>
    );
  }

  return (
    <>
      <div className={'flex justify-between'}>
        <h2>
          Meine Dozententätigkeit
          {`${myLecturerSeminars?.count ? ` (${myLecturerSeminars?.count})` : ''}`}
        </h2>
        {userInfo?.data?.currentUser?.lecturer && (
          <a
            href={webcalUrl(
              `/calendar/seminars-for-lecturer/ics?lecturerId=${userInfo?.data?.currentUser?.lecturer.id}`,
            )}
            className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md flex justify-center"
            target="_blank"
            download=""
          >
            <HeroIcon name="Calendar" className="inline-block h-5 w-5" />
            In Kalender importieren (.ics)
          </a>
        )}
      </div>
      {myLecturerSeminars?.count ? (
        myLecturerSeminars.seminars?.map((seminar) => {
          return (
            <SeminarCard
              key={seminar?.id}
              instructors={
                seminar?.lecturers
                  ?.map((lecturer) => lecturer.name)
                  .filter(Boolean) as string[]
              }
              id={seminar?.seminarNumber}
              title={seminar?.title}
              time={getFormattedSeminarDate(
                seminar.startDate,
                seminar.endDate,
                seminar.dateText,
              )}
              description={seminar?.plainDescription}
              subtitle={seminar?.subtitle}
              location={seminar?.locationText}
              category={
                seminar?.categories?.find((c) => Boolean(c.name.trim()))?.name
              }
              titleLink={'/seminar/' + seminar?.id}
            />
          );
        })
      ) : (
        <ErrorPage text="Sie geben momentan keine Seminare" />
      )}
    </>
  );
};
